import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { useFormik } from "formik";

import { UIInput, UIReactSelect, UIReactSelectNew } from "components/Inputfields";
import { EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { showSuccess, showError } from "toast/toast";

// Import API calls
import {
  fetchShippingPresetsDataTable,
  createShippingPreset,
  updateShippingPreset,
  deleteShippingPreset,
  ShippingPreset,
  getCarrierAccounts,
  type CarrierAccount,
  getPackageTemplatesSelect2,
  type PackageTemplateSelect2Result,
} from "./Shipmentsapicalls";

/* -------------------------------- TYPES -------------------------------- */

import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

type ShippingPresetsProps = {
  baseRoute: string;
};

/* ------------------------------------------------------------------------- */

const columnHelper = createColumnHelper<ShippingPreset>();

const ShippingPresets = ({ baseRoute }: ShippingPresetsProps) => {
  const [data, setData] = useState<ShippingPreset[]>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editRow, setEditRow] = useState<ShippingPreset | null>(null);

  // Delete modal state
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const fetchRequestIdRef = useRef(0);
  const hasLoadedDataRef = useRef(false);

  const token = getAuthCookie("sessionToken") || "";

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.shipping_presets
  );

  /* ------------------------------ FETCH DATA -------------------------------- */

  const fetchPresets = async () => {
    const requestId = ++fetchRequestIdRef.current;
    const showInitialLoader = !hasLoadedDataRef.current;
    if (showInitialLoader) setLoading(true);
    try {
      const result = await fetchShippingPresetsDataTable(token, {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        search: "",
        filters: {},
        sort: { field: "created_at", order: "desc" },
      });

      if (requestId !== fetchRequestIdRef.current) return;
      // API returns data directly at root level (not wrapped in success/data)
      setData(result.data || []);
      setRowCount(result.total || 0);
      hasLoadedDataRef.current = true;
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error("Error fetching presets:", err);
      showError(err instanceof Error ? err.message : "Failed to fetch shipping presets");
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPresets();
  }, [pagination.pageIndex, pagination.pageSize, deleting]);

  /* ------------------------------ DELETE -------------------------------- */

  const handleDeleteClick = (preset: ShippingPreset) => {
    setDeleteTargetId(preset._id);
    setDeleteTargetName(preset.name || "Preset");
    setOpenDelete(true);
  };

  const confirmDeletePreset = async () => {
    if (!deleteTargetId) return;
    setDeleting(deleteTargetId);

    try {
      await deleteShippingPreset(token, deleteTargetId);
      showSuccess("Shipping preset deleted successfully!");
      setData((prev) => prev.filter((preset) => preset._id !== deleteTargetId));
    } catch (err) {
      console.error("Error deleting preset:", err);
      showError(err instanceof Error ? err.message : "Failed to delete preset");
    } finally {
      setDeleting(null);
      setOpenDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetName("");
    }
  };

  /* ------------------------------ COLUMNS -------------------------------- */

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "sno",
        header: () => <div className="th-text text-center">S.No.</div>,
        cell: (info) => (
          <div className="text-center">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.accessor("name", {
        header: () => <div className="th-text">Name</div>,
        cell: (info) => <div>{info.getValue()}</div>,
      }),

      columnHelper.accessor("carrier", {
        header: () => <div className="th-text">Carrier</div>,
        cell: (info) => <div>{info.getValue()}</div>,
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text text-center">Actions</div>,
        cell: ({ row }) => (
          <div className="d-flex justify-content-center gap-2">
            {canEdit && (
            <EditIconButton
              title="Edit"
              onClick={() => {
                setEditRow(row.original);
                setShowEdit(true);
              }}
            />
            )}
            {canDelete && (
            <DeleteIconButton
              title="Delete"
              onClick={() => handleDeleteClick(row.original)}
              disabled={deleting === row.original._id}
            />
            )}
          </div>
        ),
      }),
    ],
    [pagination, deleting, canEdit, canDelete]
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: { pagination },
    manualPagination: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
  });

  const showTableLoader = loading && data.length === 0;

  /* ----------------------------------------------------------------------- */

  return (
    <div className="content container">

      {/* HEADER */}
      <div className="po-header mb-4 shadow-border-datatables">
        <div className="po-top d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h4 className="po-title mb-1">Shipping Presets</h4>
          </div>

          <div className="d-flex gap-2">
            <div className="input-group po-search">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by Name or Carrier"
              />
            </div>

            {canCreate && (
              <button
              type="button"
              className="btn btn-primary table-addbutton fs-15"
              onClick={() => setShowAdd(true)}
            >
              + &nbsp; &nbsp; Add Preset
            </button>
            )}
          </div>
        </div>
      </div>

      {/* TABLE CARD */}
      <div className="card production-card box-shadow-datatables shadow-border-datatables">
        <div className="rdt-desktop">
          <div className="production-scroll">
            <table className="table production-table align-middle mb-0">
              {showTableLoader ? (
                <DesktopTableLoader table={table} message="Loading shipping presets…" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        <div className="rdt-mobile">
          {showTableLoader ? (
            <MobileCardsLoader message="Loading shipping presets…" />
          ) : (
            <MobileCardsOnly
            table={table}
            titleColId="name"
            actionColId="actions"
          />
          )}
        </div>
      </div>

      <DataTableStatus
        loading={showTableLoader}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>

      {/* ---------------- ADD MODAL ---------------- */}
      {showAdd && (
        <PresetModal
          title="New Preset"
          onClose={() => setShowAdd(false)}
          onSuccess={async () => {
            setShowAdd(false);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
            // Refresh table after modal closes
            setTimeout(() => {
              fetchPresets();
            }, 100);
          }}
        />
      )}

      {/* ---------------- EDIT MODAL ---------------- */}
      {showEdit && editRow && (
        <PresetModal
          title="Edit Preset"
          defaultValues={editRow}
          onClose={() => {
            setShowEdit(false);
            setEditRow(null);
          }}
          onSuccess={async () => {
            setShowEdit(false);
            setEditRow(null);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
            // Refresh table after modal closes
            setTimeout(() => {
              fetchPresets();
            }, 100);
          }}
        />
      )}

      {/* ---------------- DELETE CONFIRM MODAL ---------------- */}
      <DeleteConfirmModal
        open={openDelete}
        dynamicName={deleteTargetName || "Shipping Preset"}
        onCancel={() => {
          setOpenDelete(false);
          setDeleteTargetId(null);
          setDeleteTargetName("");
        }}
        onDelete={confirmDeletePreset}
      />
    </div>
  );
};

/* --------------------------- MODAL COMPONENT ----------------------------- */

type Warehouse = {
  _id: string;
  label: string;
  name?: string;
  warehouse_name?: string;
  is_default?: boolean;
  address?: any;
  status?: boolean;
};

const PresetModal = ({
  title,
  onClose,
  onSuccess,
  defaultValues,
}: {
  title: string;
  onClose: () => void;
  onSuccess: () => void;
  defaultValues?: ShippingPreset;
}) => {
  const token = getAuthCookie("sessionToken") || "";
  const [submitting, setSubmitting] = useState(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [carrierAccounts, setCarrierAccounts] = useState<CarrierAccount[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packageTemplates, setPackageTemplates] = useState<PackageTemplateSelect2Result[]>([]);
  const prevCarrierRef = useRef<string | null>(null);
  const carrierIdInitializedRef = useRef(false);

  // Fetch warehouses on mount
  useEffect(() => {
    const fetchWarehouses = async () => {
      setLoadingWarehouses(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/warehouses?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json().catch(() => null);
        const list =
          (Array.isArray(data) ? data : null) ||
          (Array.isArray(data?.data) ? data.data : null) ||
          [];
        setWarehouses(list);
      } catch (err) {
        console.error("Error fetching warehouses:", err);
      } finally {
        setLoadingWarehouses(false);
      }
    };

    fetchWarehouses();
  }, [token]);

  // Fetch carriers for Carrier dropdown (dynamic)
  useEffect(() => {
    if (!token) return;
    const run = async () => {
      setLoadingCarriers(true);
      try {
        const list = await getCarrierAccounts(token);
        setCarrierAccounts(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        showError("Failed to load carriers");
        setCarrierAccounts([]);
      } finally {
        setLoadingCarriers(false);
      }
    };
    run();
  }, [token]);

  // Convert warehouses to dropdown options
  const warehouseOptions = warehouses.map((wh) => ({
    value: wh._id,
    label: (wh as any)?.label || (wh as any)?.name || (wh as any)?.warehouse_name || "Warehouse",
  }));

  const carrierOptions = useMemo(
    () =>
      (carrierAccounts || []).map((c) => ({
        value: c._id,
        label: c.carrier_name || "Carrier",
      })),
    [carrierAccounts]
  );

  const resolveCarrierAccount = useCallback(
    (preset?: ShippingPreset) => {
      if (!preset || carrierAccounts.length === 0) return undefined;
      if (preset.carrier_account_id) {
        const byAccountId = carrierAccounts.find(
          (a) => a.carrier_account_id === preset.carrier_account_id
        );
        if (byAccountId) return byAccountId;
      }
      if (preset.carrier) {
        return carrierAccounts.find(
          (a) =>
            (a.carrier_name || "").toLowerCase() === (preset.carrier || "").toLowerCase()
        );
      }
      return undefined;
    },
    [carrierAccounts]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      presetName: defaultValues?.name || "",
      carrierId: resolveCarrierAccount(defaultValues)?._id || "",
      packaging: defaultValues?.packaging_type || "",
      shipFrom: defaultValues?.ship_from_warehouse_id || "",
      length: defaultValues?.package?.length || "",
      width: defaultValues?.package?.width || "",
      height: defaultValues?.package?.height || "",
      dimension_unit: defaultValues?.package?.dimension_unit || "in",
      weight: defaultValues?.package?.weight || "",
    },
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        const selectedCarrier = carrierAccounts.find((c) => c._id === values.carrierId);
        if (!selectedCarrier) {
          showError("Please select a carrier");
          setSubmitting(false);
          return;
        }

        const selectedTemplate =
          values.packaging && values.packaging !== "own_packaging"
            ? packageTemplates.find((t) => t.id === values.packaging) || null
            : null;

        const payload: any = {
          name: values.presetName,
          carrier: selectedCarrier.carrier_name,
          carrier_account_id: selectedCarrier.carrier_account_id,
          packaging_type:
            values.packaging === "own_packaging"
              ? "own_packaging"
              : selectedTemplate?.name || values.packaging || "",
          status: "active",
        };

        // ✅ Package rules:
        // - Own package: user inputs L/W/H (+ optional weight)
        // - Template: use template dimensions
        if (values.packaging === "own_packaging") {
          payload.package = {
            length: Number(values.length) || 0,
            width: Number(values.width) || 0,
            height: Number(values.height) || 0,
            dimension_unit: values.dimension_unit || "in",
            weight: Number(values.weight) || 0,
            weight_unit: "lb",
          };
        } else if (selectedTemplate?.dimensions) {
          payload.package = {
            length: Number(selectedTemplate.dimensions.length) || 0,
            width: Number(selectedTemplate.dimensions.width) || 0,
            height: Number(selectedTemplate.dimensions.height) || 0,
            dimension_unit: "in",
            weight: Number(selectedTemplate.dimensions.weight) || 0,
            weight_unit: "lb",
          };
        }

        // Add ship_from_warehouse_id if selected
        if (values.shipFrom) {
          payload.ship_from_warehouse_id = values.shipFrom;
        }

        let result;
        if (defaultValues?._id) {
          result = await updateShippingPreset(token, defaultValues._id, payload);
          // console.log("Update preset response:", result);
          showSuccess("Preset updated successfully");
        } else {
          result = await createShippingPreset(token, payload);
          // console.log("Create preset response:", result);
          showSuccess("Preset created successfully");
        }

        // Close modal and refresh table - onSuccess handles both
        // Use setTimeout to ensure state updates happen after success message
        setTimeout(() => {
          onSuccess();
        }, 0);
      } catch (err) {
        console.error("Error saving preset:", err);
        console.error("Error details:", {
          message: err instanceof Error ? err.message : String(err),
          stack: err instanceof Error ? err.stack : undefined,
        });
        showError(err instanceof Error ? err.message : "Failed to save preset");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Set carrier dropdown when editing (after carrier accounts load)
  useEffect(() => {
    if (!defaultValues || loadingCarriers || carrierAccounts.length === 0) return;
    if (carrierIdInitializedRef.current) return;

    const acc = resolveCarrierAccount(defaultValues);
    if (acc) {
      formik.setFieldValue("carrierId", acc._id);
      carrierIdInitializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, carrierAccounts, loadingCarriers, resolveCarrierAccount]);

  // Load packages after carrier selected
  useEffect(() => {
    const selectedCarrier = carrierAccounts.find((c) => c._id === formik.values.carrierId);
    const carrier = String(selectedCarrier?.carrier_name || "").trim();
    if (!token) return;

    const prev = prevCarrierRef.current;
    prevCarrierRef.current = formik.values.carrierId || null;

    // Clear selection only when the user actually changes carrier (not initial edit render)
    if (prev !== null && prev !== formik.values.carrierId) {
      formik.setFieldValue("packaging", "");
      formik.setFieldValue("length", "");
      formik.setFieldValue("width", "");
      formik.setFieldValue("height", "");
      formik.setFieldValue("weight", "");
    }

    setPackageTemplates([]);
    if (!carrier) return;

    const run = async () => {
      setLoadingPackages(true);
      try {
        const { results } = await getPackageTemplatesSelect2(token, {
          q: "",
          page: 1,
          limit: 50,
          status: "active",
          carrier,
        });
        setPackageTemplates(results || []);
      } catch (e) {
        console.error(e);
        showError("Failed to load packages");
        setPackageTemplates([]);
      } finally {
        setLoadingPackages(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.carrierId, carrierAccounts]);

  const packagingOptions = useMemo(() => {
    const own = { value: "own_packaging", label: "Own package" };
    const templates = (packageTemplates || []).map((t) => ({
      value: t.id,
      label: t.name || t.text,
    }));
    return [own, ...templates];
  }, [packageTemplates]);

  // When a template is selected, store its dimensions in state (no manual UI shown)
  useEffect(() => {
    const v = String(formik.values.packaging || "");
    if (!v || v === "own_packaging") return;
    const t = packageTemplates.find((x) => x.id === v);
    if (!t?.dimensions) return;
    formik.setFieldValue("length", String(t.dimensions.length ?? ""));
    formik.setFieldValue("width", String(t.dimensions.width ?? ""));
    formik.setFieldValue("height", String(t.dimensions.height ?? ""));
    formik.setFieldValue("weight", String(t.dimensions.weight ?? ""));
    formik.setFieldValue("dimension_unit", "in");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.packaging, packageTemplates]);

  const showOwnPackaging = formik.values.packaging === "own_packaging";

  return (
    <div className="modal-overlay active">
      <div className="modal-wrapper">

        {/* HEADER */}
        <div className="modal-header-custom">
          <h5>{title}</h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        {/* FORM */}
        <form className="modal-form" onSubmit={formik.handleSubmit}>

          {/* BODY */}
          <div className="modal-body-custom">
            <div className="preset-form">

              {/* Preset Name */}
              <div className="row">
                <div className="preset-row col-12">
                  <div className="preset-control">
                    <UIInput
                      formik={formik}
                      name="presetName"
                      label="Preset Name"
                      placeholder=""
                    />
                  </div>
                </div>
              </div>

              {/* Carrier */}
              <div className="preset-row col-6">
                <div className="preset-control">
                  <UIReactSelectNew
                    formik={formik}
                    name="carrierId"
                    label="Carrier"
                    options={carrierOptions}
                    placeholder={loadingCarriers ? "Loading carriers..." : "Select carrier"}
                    disabled={loadingCarriers}
                  />
                </div>
              </div>

              {/* Packaging */}
              <div className="preset-row col-6">
                <div className="preset-control">
                  <UIReactSelectNew
                    formik={formik}
                    name="packaging"
                    label="Packaging"
                    options={packagingOptions}
                    placeholder={loadingPackages ? "Loading packages..." : "Select package"}
                    disabled={!formik.values.carrierId || loadingPackages}
                  />
                </div>
              </div>

              {/* Own Packaging (ONLY when selected) */}
              {showOwnPackaging && (
                <div className="preset-row">
                  <div className="preset-label mb-2">Own Packaging</div>
                  <div className="preset-control">
                    <div className="own-packaging-inline">
                      <div className="own-packaging-item">
                        <span className="own-packaging-prefix">L:</span>
                        <input
                          className="own-packaging-input"
                          name="length"
                          value={formik.values.length}
                          onChange={formik.handleChange}
                          placeholder="length - in"
                        />
                      </div>
                      <div className="own-packaging-item">
                        <span className="own-packaging-prefix">W:</span>
                        <input
                          className="own-packaging-input"
                          name="width"
                          value={formik.values.width}
                          onChange={formik.handleChange}
                          placeholder="width - in"
                        />
                      </div>
                      <div className="own-packaging-item">
                        <span className="own-packaging-prefix">H:</span>
                        <input
                          className="own-packaging-input"
                          name="height"
                          value={formik.values.height}
                          onChange={formik.handleChange}
                          placeholder="height - in"
                        />
                      </div>
                    </div>
                    <input type="hidden" name="dimension_unit" value={formik.values.dimension_unit || "in"} />
                    <input type="hidden" name="weight" value={formik.values.weight || ""} />
                  </div>
                </div>
              )}

              {/* Ship From */}
              <div className="preset-row col-6">
                <div className="preset-label">Ship From</div>
                <div className="preset-control preset-control-shipfrom">
                  <UIReactSelect
                    formik={formik}
                    name="shipFrom"
                    label=""
                    options={warehouseOptions}
                    placeholder={loadingWarehouses ? "Loading warehouses..." : "Select Location"}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="modal-footer-custom">
            <button
              type="button"
              className="btn back-btn surface-btn fw-bold fs-16"
              onClick={onClose}
              disabled={submitting}
            >
              <span className="me-2">&#x2039;</span> Cancel
            </button>

            <button
              type="submit"
              className="btn save-btn surface-btn fw-bold fs-16"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
      <style>
        {`.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-wrapper {
  background: #fff;
  width: 100%;
  max-width: 640px;
  max-height: 90vh;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header-custom {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-header-custom h5 {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.modal-body-custom {
  padding: 18px 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  position: relative;
  z-index: 2;
}

.modal-footer-custom {
  padding: 14px 20px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  background: #fff;
  flex-shrink: 0;
  gap: 10px;
  position: relative;
  z-index: 1;
}


/* ---------- UI like screenshot (label-left + compact controls) ---------- */
.preset-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preset-row {
  // display: grid;
  grid-template-columns: 150px 1fr;
  align-items: center;
  gap: 18px;
}

.preset-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
}

.preset-control :is(input, select) {
  width: 100%;
}

/* Make UIInput/UIReactSelect look compact in this modal */
.preset-control .form-control,
.preset-control .react-select__control {
  border-radius: 10px;
}

/* Ship From dropdown needs higher z-index so menu appears above other content */
.preset-control-shipfrom {
  position: relative;
  z-index: 10;
}

.own-packaging-inline {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.own-packaging-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 6px 10px;
}

.own-packaging-prefix {
  font-weight: 700;
  color: #374151;
  font-size: 0.85rem;
}

.own-packaging-input {
  width: 110px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.85rem;
  color: #111827;
}

.cancel-btn {
  border-radius: 10px;
  padding: 10px 16px;
}

.save-btn {
  background: #00a63e;
  border: 1px solid #16a34a;
  color: #fff;
  border-radius: 10px;
  padding: 10px 22px;
  min-width: 110px;
  font-weight: 600;
}

.save-btn:hover {
  background: #008236 !important;
  border-color: #15803d !important;
  color: #fff;
}
  .own-packaging-inline { flex-wrap: nowrap; }
@media (max-width: 576px) {
  .own-packaging-inline { flex-wrap: wrap; }
}


        `}
      </style>
    </div>
  );
};



export default ShippingPresets;
